import * as React from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import {
  createSchedule,
  listTools,
  type ScheduleCreateInput,
  type ScheduleKind,
  type ToolInfo,
} from "../../api/schedules";
import { buildCron, WEEKDAYS, type RecurrencePreset } from "./cron";
import { useNotificationsOptional } from "@/notifications/NotificationsProvider";
import { PortalScope, usePortalScope } from "@/analytics/PortalScope";

interface Props {
  open: boolean;
  onClose: () => void;
  /** Called after a successful create so the list can refresh. */
  onCreated: () => void;
}

type Mode = "once" | "recurring" | "event";

/** Local datetime-input value → ISO-8601 UTC (what the backend's run_at wants).
 *
 *  A <input type="datetime-local"> yields a naive wall-clock string
 *  ("2026-06-17T10:00", no zone). Passing that straight to `new Date(str)` is
 *  unreliable: spec-compliant engines read it as local, but some (older Safari,
 *  certain embedded webviews) read a naive date-time as UTC — which silently
 *  shifts the stored instant by the zone offset (the "set 10 AM, shows 3 AM"
 *  bug). We avoid the ambiguity entirely by parsing the components and building
 *  the Date via the multi-arg constructor, which is ALWAYS interpreted in local
 *  time on every engine. */
export function localToIsoUtc(localValue: string): string | null {
  if (!localValue) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(localValue.trim());
  if (!m) return null;
  const [, y, mo, d, h, mi, s] = m;
  const dt = new Date(
    Number(y),
    Number(mo) - 1,
    Number(d),
    Number(h),
    Number(mi),
    s ? Number(s) : 0,
    0,
  );
  if (Number.isNaN(dt.getTime())) return null;
  return dt.toISOString();
}

export function ScheduleFormDialog({ open, onClose, onCreated }: Props) {
  const notifications = useNotificationsOptional();
  // The selects' menus and the tool picker's list portal outside the dialog,
  // so each declares its full path (spec D7), under its trigger's section.
  const frequencyMenuScope = usePortalScope("create_dialog-frequency");
  const dayMenuScope = usePortalScope("create_dialog-day");
  const toolsPopperScope = usePortalScope("create_dialog-tools");
  const [name, setName] = React.useState("");
  const [prompt, setPrompt] = React.useState("");
  const [mode, setMode] = React.useState<Mode>("recurring");

  // one-time
  const [runAtLocal, setRunAtLocal] = React.useState("");
  // recurring
  const [preset, setPreset] = React.useState<RecurrencePreset>("daily");
  const [time, setTime] = React.useState("09:00");
  const [weekday, setWeekday] = React.useState(1);
  // event
  const [threshold, setThreshold] = React.useState(10);

  // tools
  const [allTools, setAllTools] = React.useState<ToolInfo[]>([]);
  const [tools, setTools] = React.useState<string[]>([]);

  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Load the tool catalog once when the dialog opens.
  React.useEffect(() => {
    if (!open) return;
    listTools().then(setAllTools).catch(() => setAllTools([]));
  }, [open]);

  // Reset the form each time it opens fresh.
  React.useEffect(() => {
    if (open) {
      setName(""); setPrompt(""); setMode("recurring");
      setRunAtLocal(""); setPreset("daily"); setTime("09:00"); setWeekday(1);
      setThreshold(10); setTools([]); setError(null); setSubmitting(false);
    }
  }, [open]);

  const buildPayload = (): ScheduleCreateInput | { error: string } => {
    if (!name.trim()) return { error: "Give the task a name." };
    if (!prompt.trim()) return { error: "Describe what the task should do." };

    // Browser IANA timezone so the backend anchors cron wall-clock times in
    // the user's zone (and stores it for display). Best-effort; omitted if
    // the platform can't report it.
    let tz: string | undefined;
    try {
      tz = Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
    } catch {
      tz = undefined;
    }
    const base = { name: name.trim(), prompt: prompt.trim(), tools, ...(tz ? { tz } : {}) };

    if (mode === "once") {
      const iso = localToIsoUtc(runAtLocal);
      if (!iso) return { error: "Pick a valid date and time." };
      if (new Date(iso).getTime() <= Date.now() + 60_000) {
        return { error: "Pick a time at least a minute in the future." };
      }
      return { ...base, kind: "one_shot" as ScheduleKind, run_at: iso };
    }

    if (mode === "recurring") {
      const [h, m] = time.split(":").map(Number);
      const cron_expr = buildCron(preset, { hour: h || 0, minute: m || 0, weekday });
      return { ...base, kind: "cron" as ScheduleKind, cron_expr };
    }

    // event
    if (threshold < 1) return { error: "Threshold must be at least 1." };
    return {
      ...base,
      kind: "event" as ScheduleKind,
      event_kind: "dialogue_count",
      event_threshold: threshold,
    };
  };

  const submit = async () => {
    const payload = buildPayload();
    if ("error" in payload) { setError(payload.error); return; }
    setError(null);
    setSubmitting(true);
    try {
      await createSchedule(payload);
      notifications?.notify(`Scheduled “${payload.name}”`, "success");
      onCreated();
      onClose();
    } catch (e) {
      setError(`Couldn’t create the schedule: ${String(e)}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      {/* Portaled, so it declares its path: a dialog is its own section. */}
      <PortalScope section="create_dialog">
        <DialogTitle>New scheduled task</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 0.5 }}>
            {error && <Alert severity="error">{error}</Alert>}

            <TextField
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Weekly inventory report"
              fullWidth
              autoFocus
            />

            <TextField
              label="Task"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Summarize this week’s inventory changes and flag anything low."
              fullWidth
              multiline
              minRows={3}
              helperText="What should the assistant do each time this runs?"
            />

            <Box>
              <Typography variant="overline" sx={{ color: "text.secondary" }}>
                When
              </Typography>
              <ToggleButtonGroup
                value={mode}
                exclusive
                onChange={(_e, v) => v && setMode(v)}
                size="small"
                sx={{ display: "flex", mt: 0.5 }}
              >
                <ToggleButton value="once" data-ph="mode-select_once" sx={{ flex: 1 }}>Once</ToggleButton>
                <ToggleButton value="recurring" data-ph="mode-select_recurring" sx={{ flex: 1 }}>Repeats</ToggleButton>
                <ToggleButton value="event" data-ph="mode-select_event" sx={{ flex: 1 }}>On activity</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {mode === "once" && (
              <TextField
                label="Run at"
                type="datetime-local"
                value={runAtLocal}
                onChange={(e) => setRunAtLocal(e.target.value)}
                fullWidth
                slotProps={{ inputLabel: { shrink: true } }}
                helperText="Your local time."
              />
            )}

            {mode === "recurring" && (
              <Stack direction="row" spacing={1.5}>
                <TextField
                  select
                  label="Frequency"
                  value={preset}
                  onChange={(e) => setPreset(e.target.value as RecurrencePreset)}
                  sx={{ flex: 1 }}
                  data-ph="frequency-open"
                  slotProps={{ select: { MenuProps: { slotProps: { list: frequencyMenuScope } } } }}
                >
                  <MenuItem value="hourly" data-ph="select_hourly">Hourly</MenuItem>
                  <MenuItem value="daily" data-ph="select_daily">Daily</MenuItem>
                  <MenuItem value="weekly" data-ph="select_weekly">Weekly</MenuItem>
                </TextField>
                {preset === "weekly" && (
                  <TextField
                    select
                    label="Day"
                    value={weekday}
                    onChange={(e) => setWeekday(Number(e.target.value))}
                    sx={{ flex: 1 }}
                    data-ph="day-open"
                    slotProps={{ select: { MenuProps: { slotProps: { list: dayMenuScope } } } }}
                  >
                    {WEEKDAYS.map((d) => (
                      <MenuItem key={d.value} value={d.value} data-ph="select">{d.label}</MenuItem>
                    ))}
                  </TextField>
                )}
                {preset !== "hourly" && (
                  <TextField
                    label="Time"
                    type="time"
                    value={time}
                    onChange={(e) => setTime(e.target.value)}
                    sx={{ flex: 1 }}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                )}
              </Stack>
            )}

            {mode === "event" && (
              <TextField
                label="After this many chat turns"
                type="number"
                value={threshold}
                onChange={(e) => setThreshold(Number(e.target.value))}
                fullWidth
                slotProps={{ htmlInput: { min: 1 } }}
                helperText="Runs once your conversation activity reaches this count, then resets."
              />
            )}

            <Autocomplete
              multiple
              options={allTools.map((t) => t.name)}
              value={tools}
              onChange={(_e, v) => setTools(v)}
              data-ph="tools-open"
              // Options are tagged by kind: the listbox carries the tag, so a
              // click on any option resolves to it (never the tool's name).
              // Chips and the clear button sit inside the root, so each carries
              // its own tag rather than reading as the root's `open`.
              slotProps={{
                popper: toolsPopperScope,
                listbox: { "data-ph": "select" } as React.HTMLAttributes<HTMLUListElement>,
                chip: { "data-ph": "tools-delete" } as Record<string, string>,
                clearIndicator: { "data-ph": "tools-delete_all" } as Record<string, string>,
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Tools"
                  placeholder={tools.length ? "" : "Pick tools the task may use"}
                  helperText="Which capabilities the scheduled assistant can use."
                />
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button data-ph="cancel" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button data-ph="submit" onClick={submit} variant="contained" disabled={submitting}>
            {submitting ? "Creating…" : "Create"}
          </Button>
        </DialogActions>
      </PortalScope>
    </Dialog>
  );
}
